import { CartAddEvent } from '@theme/events';

const ROOT = window.Shopify?.routes?.root || '/';
const CART_URL = `${ROOT}cart.js`;
const CART_ADD_URL = `${ROOT}cart/add.js`;
const TRACKING_VERSION = 'aov-v1';

async function fetchCart() {
  const response = await fetch(CART_URL, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
  return response.json();
}

function trackingSource(context) {
  return context === 'cart' ? 'cart_upsell' : 'fbt_product';
}

function trackingProperties(context, anchorProductId) {
  return {
    _isarttools_source: trackingSource(context),
    _isarttools_version: TRACKING_VERSION,
    _isarttools_anchor_product: String(anchorProductId || ''),
  };
}

function attributedUrl(url, context, anchorProductId) {
  try {
    const target = new URL(url, window.location.origin);
    target.searchParams.set('iast_source', trackingSource(context));
    target.searchParams.set('iast_anchor', String(anchorProductId || ''));
    target.searchParams.set('iast_version', TRACKING_VERSION);
    return target.toString();
  } catch (_) {
    return url;
  }
}

function emitAovEvent(action, detail = {}) {
  const payload = {
    action,
    source: detail.source || '',
    context: detail.context || '',
    anchorProductId: String(detail.anchorProductId || ''),
    offerProductId: String(detail.offerProductId || ''),
    variantId: String(detail.variantId || ''),
    version: TRACKING_VERSION,
  };

  window.dispatchEvent(new CustomEvent('isarttools:aov', { detail: payload }));

  if (Array.isArray(window.dataLayer)) {
    window.dataLayer.push({ event: `isarttools_aov_${action}`, ...payload });
  }

  if (typeof window.fbq === 'function') {
    try {
      window.fbq('trackCustom', action === 'add' ? 'IsarttoolsAOVAdd' : 'IsarttoolsAOVClick', payload);
    } catch (_) {}
  }
}

function createCartWidget(productId, digitalOnly) {
  const widget = document.createElement('isarttools-aov-upsell');
  widget.className = 'isarttools-aov';
  widget.dataset.productId = String(productId);
  widget.dataset.context = 'cart';
  widget.dataset.digital = String(digitalOnly);
  widget.hidden = true;
  widget.innerHTML = `
    <div class="isarttools-aov__header">
      <strong class="isarttools-aov__title">كمّل تجهيزتك</strong>
      <span class="isarttools-aov__hint">إضافات مختارة تكمل طلبك</span>
    </div>
    <div class="isarttools-aov__products" data-aov-products></div>
  `;
  return widget;
}

class IsarttoolsAovUpsell extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';
    this.mountProductWidget();
    this.load();
  }

  mountProductWidget() {
    if (this.dataset.context !== 'product') return;
    const buyBlock = document.querySelector('.product-information .buy-buttons-block');
    if (buyBlock && buyBlock.nextElementSibling !== this) buyBlock.after(this);
  }

  async load() {
    const productId = Number(this.dataset.productId || 0);
    if (!productId) return;

    try {
      const cart = await fetchCart();
      const inCart = new Set((cart.items || []).map((item) => Number(item.product_id)));
      let eligible = this.filterProducts(await this.fetchRecommendations(productId, 'complementary'), inCart, productId);

      if (eligible.length < 2) {
        const fallback = this.filterProducts(await this.fetchRecommendations(productId, 'related'), inCart, productId);
        const seen = new Set(eligible.map((item) => Number(item.id)));
        for (const item of fallback) {
          if (!seen.has(Number(item.id))) eligible.push(item);
          if (eligible.length >= 2) break;
        }
      }

      this.render(eligible.slice(0, 2));
    } catch (error) {
      console.warn('[Isarttools AOV] Recommendations unavailable', error);
      this.hidden = true;
    }
  }

  async fetchRecommendations(productId, intent) {
    const response = await fetch(
      `${ROOT}recommendations/products.json?product_id=${productId}&limit=6&intent=${intent}`,
      { headers: { Accept: 'application/json' }, credentials: 'same-origin' }
    );
    if (!response.ok) return [];
    const data = await response.json();
    return Array.isArray(data.products) ? data.products : [];
  }

  filterProducts(products, inCart, anchorId) {
    const digitalOnly = this.dataset.digital === 'true';
    return products.filter((product) => {
      if (!product || product.available === false) return false;
      if (Number(product.id) === anchorId || inCart.has(Number(product.id))) return false;

      const available = (product.variants || []).filter((variant) => variant.available !== false);
      if (!available.length) return false;

      if (digitalOnly) {
        const tags = Array.isArray(product.tags)
          ? product.tags
          : String(product.tags || '').split(',').map((tag) => tag.trim());
        const hasShippingInfo = available.some((variant) => 'requires_shipping' in variant);
        const digitalVariant = available.some((variant) => variant.requires_shipping === false);
        if (hasShippingInfo && !digitalVariant) return false;
        if (!hasShippingInfo && !tags.includes('visa-only-digital')) return false;
      }
      return true;
    });
  }

  getVariant(product) {
    const available = (product.variants || []).filter((variant) => variant.available !== false);
    if (this.dataset.digital === 'true') {
      return available.find((variant) => variant.requires_shipping === false) || available[0] || null;
    }
    return available[0] || null;
  }

  hasChoice(product) {
    const available = (product.variants || []).filter((variant) => variant.available !== false);
    if (available.length <= 1) return false;
    return (product.options || []).some((option) => String(option?.name || option).toLowerCase() !== 'title');
  }

  eventDetail(productId, variantId = '') {
    const context = this.dataset.context || 'product';
    return {
      source: trackingSource(context),
      context,
      anchorProductId: this.dataset.productId || '',
      offerProductId: productId || '',
      variantId,
    };
  }

  render(products) {
    const container = this.querySelector('[data-aov-products]');
    if (!container || !products.length) {
      this.hidden = true;
      return;
    }
    container.replaceChildren(...products.map((product) => this.createCard(product)));
    this.hidden = false;
  }

  createCard(product) {
    const card = document.createElement('div');
    card.className = 'isarttools-aov__card';
    const rawUrl = product.url || `${ROOT}products/${product.handle}`;
    const url = attributedUrl(rawUrl, this.dataset.context, this.dataset.productId);

    const imageLink = document.createElement('a');
    imageLink.className = 'isarttools-aov__image-link';
    imageLink.href = url;
    imageLink.addEventListener('click', () => emitAovEvent('click', this.eventDetail(product.id)));
    if (product.featured_image) {
      const image = document.createElement('img');
      image.className = 'isarttools-aov__image';
      image.src = product.featured_image;
      image.alt = product.title || '';
      image.loading = 'lazy';
      image.width = 140;
      image.height = 140;
      imageLink.append(image);
    }

    const info = document.createElement('div');
    info.className = 'isarttools-aov__info';
    const name = document.createElement('a');
    name.className = 'isarttools-aov__name';
    name.href = url;
    name.textContent = product.title || '';
    name.addEventListener('click', () => emitAovEvent('click', this.eventDetail(product.id)));
    const variant = this.getVariant(product);
    const price = document.createElement('span');
    price.className = 'isarttools-aov__price';
    price.textContent = this.formatMoney(Number(variant?.price ?? product.price ?? 0));
    info.append(name, price);

    let action;
    if (!variant || this.hasChoice(product)) {
      action = document.createElement('a');
      action.href = url;
      action.textContent = 'اختيار';
      action.addEventListener('click', () => emitAovEvent('click', this.eventDetail(product.id)));
    } else {
      action = document.createElement('button');
      action.type = 'button';
      action.textContent = '+ إضافة';
      action.addEventListener('click', () => this.addVariant(action, variant.id, product.id));
    }
    action.className = 'isarttools-aov__action';
    card.append(imageLink, info, action);
    return card;
  }

  formatMoney(cents) {
    const currency = window.Shopify?.currency?.active || 'EGP';
    const locale = document.documentElement.lang || 'ar-EG';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency, maximumFractionDigits: 2 }).format(cents / 100);
    } catch (_) {
      return `${(cents / 100).toFixed(2)} ${currency}`;
    }
  }

  async addVariant(button, variantId, offerProductId) {
    if (!variantId || button.disabled) return;
    emitAovEvent('click', this.eventDetail(offerProductId, variantId));
    button.disabled = true;
    const original = button.textContent;
    button.textContent = 'جارٍ الإضافة…';

    try {
      const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
        .map((element) => element.dataset.sectionId)
        .filter(Boolean);
      const response = await fetch(CART_ADD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          items: [{
            id: Number(variantId),
            quantity: 1,
            properties: trackingProperties(this.dataset.context, this.dataset.productId),
          }],
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const detail = await response.json();
      if (!response.ok || detail.status) throw new Error(detail.description || 'Unable to add item');

      const cart = await fetchCart();
      emitAovEvent('add', this.eventDetail(offerProductId, variantId));
      this.dispatchEvent(new CartAddEvent(cart, `isarttools-aov-${variantId}`, {
        source: 'isarttools-aov-upsell',
        itemCount: cart.item_count,
        variantId: String(variantId),
        sections: detail.sections || {},
      }));

      button.textContent = 'تمت الإضافة ✓';
      window.setTimeout(() => {
        if (this.isConnected) this.load();
        scheduleCartUpsell();
      }, 350);
    } catch (error) {
      console.warn('[Isarttools AOV] Add failed', error);
      button.textContent = 'تعذر الإضافة';
      window.setTimeout(() => {
        if (button.isConnected) {
          button.disabled = false;
          button.textContent = original;
        }
      }, 1300);
      return;
    }

    window.setTimeout(() => {
      if (button.isConnected) button.disabled = false;
    }, 550);
  }
}

if (!customElements.get('isarttools-aov-upsell')) {
  customElements.define('isarttools-aov-upsell', IsarttoolsAovUpsell);
}

let cartTimer;
async function ensureCartUpsell() {
  const drawer = document.querySelector('cart-drawer-component');
  const summary = drawer?.querySelector('.cart-drawer__summary');
  if (!drawer || !summary) return;

  try {
    const cart = await fetchCart();
    const existing = drawer.querySelector('isarttools-aov-upsell[data-context="cart"]');
    if (!cart.item_count || !cart.items?.length) {
      existing?.remove();
      return;
    }

    const anchor = cart.items[0];
    const productId = Number(anchor.product_id || 0);
    if (!productId) return;
    if (existing && Number(existing.dataset.productId) === productId) return;
    existing?.remove();
    summary.before(createCartWidget(productId, anchor.requires_shipping === false));
  } catch (error) {
    console.warn('[Isarttools AOV] Cart upsell unavailable', error);
  }
}

function scheduleCartUpsell() {
  window.clearTimeout(cartTimer);
  cartTimer = window.setTimeout(ensureCartUpsell, 220);
}

document.addEventListener(CartAddEvent.eventName, scheduleCartUpsell);
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', scheduleCartUpsell, { once: true });
} else {
  scheduleCartUpsell();
}
